import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipEditedEvent, MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { TagService } from '../../services/tag.service';
import { StoryTag } from '../../model/story/story-tag.model';
import { MatInputModule } from '@angular/material/input';
import { CommonModule } from '@angular/common';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { BehaviorSubject, debounceTime, Observable, startWith, Subject, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-story-tags',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatInputModule,
    FormsModule,
  ],
  templateUrl: './story-tags.html',
  styleUrl: './story-tags.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StoryTags implements OnChanges {
  @Input() storySelectedTagList: StoryTag[] = [];
  public valueChange = output<StoryTag[]>();

  @ViewChild('tagInput') tagInput!: ElementRef<HTMLInputElement>;
  private readonly tagService = inject(TagService);

  public selectedItemList: StoryTag[] = [];
  private selectedItemIdList: string[] = [];
  public inputFormControl = new FormControl<
    string | StoryTag
  >('');
  public separatorKeysCodes: number[] = [ENTER, COMMA];
  private resultFromServiceSubject: BehaviorSubject<StoryTag[]> =
    new BehaviorSubject<StoryTag[]>([]);
  public resultFromService$: Observable<StoryTag[]> =
    this.resultFromServiceSubject.asObservable();

  private optionBeingSelected = false;
  private tokenEndObservable = new Subject<MatChipInputEvent>();
  private tokenEnd$: Observable<MatChipInputEvent> = this.tokenEndObservable.asObservable();

  constructor() {
    this.observeInputFormControlChanges();
    this.observeTokenEnd();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['storySelectedTagList']) {
      this.selectedItemList = changes['storySelectedTagList'].currentValue;
      this.selectedItemIdList = this.selectedItemList.map((item) => item.id);
    }
  }

  private observeInputFormControlChanges(): void {
    this.inputFormControl?.valueChanges
      .pipe(
        takeUntilDestroyed(),
        startWith(''),
        debounceTime(200),
        switchMap((value: string | StoryTag | null) => {
          const queryString: string =
            typeof value === 'string'
              ? value
              : value === null
                ? ''
                : value.name;
          return this.tagService.searchTag(queryString).pipe(
            // Don't include the items that are already selected in the result
            map((storyTagList: StoryTag[]): StoryTag[] =>
              storyTagList.filter(
                (tag: StoryTag): boolean =>
                  !this.selectedItemList.some(
                    ({id}: StoryTag): boolean => id === tag.id,
                  ),
              ),
            ),
          );
        }),
      )
      .subscribe((tagList: StoryTag[]): void => {
        // Emit the possible values from the service with a given query
        this.resultFromServiceSubject.next(tagList);
      });
  }

  public editedMatChipRow(
    event: MatChipEditedEvent,
    itemToUpdate: StoryTag,
  ): void {
    const newStory = new StoryTag({
      id: 'pending-to-create',
      name: event.value,
    });
    this.selectedItemList = this.selectedItemList.map(
      (story: StoryTag): StoryTag =>
        story.name === itemToUpdate.name
          ? newStory
          : story,
    );
  }

  public inputTokenEnd(event: MatChipInputEvent): void {
    this.tokenEndObservable.next(event);
    this.clearChipInput();
  }

  private observeTokenEnd(): void {
    this.tokenEnd$.pipe(
      takeUntilDestroyed(),
      debounceTime(10),
    ).subscribe((event: MatChipInputEvent): void => {
      const newValue: string = event.value.trim();
      if (this.optionBeingSelected === true) {
        this.optionBeingSelected = false;
        return;
      }
      if (newValue) {
        const valueMatchWithAutocomplete: StoryTag | undefined =
          this.resultFromServiceSubject.value.find(
            ({name}: StoryTag): boolean =>
              name.toLocaleLowerCase() === newValue.toLocaleLowerCase(),
          );
        if (valueMatchWithAutocomplete) {
          this.selectedItemList.push(valueMatchWithAutocomplete);
          this.selectedItemIdList.push(valueMatchWithAutocomplete.id);
          this.valueChange.emit(this.selectedItemList);
        } else {
          const valueAlreadyAdded: StoryTag | undefined = this.selectedItemList.find(
            ({name}: StoryTag): boolean =>
              name.toLocaleLowerCase() === newValue.toLocaleLowerCase(),
          );
          if (!valueAlreadyAdded) {
            // Create new StoryTag
            this.tagService.createTag(newValue).subscribe((newStoryTag: StoryTag) =>
              this.selectedItemList.push(newStoryTag),
            );
            this.valueChange.emit(this.selectedItemList);
          }
        }
      }
      this.optionBeingSelected = false;
    });
  }

  public clearChipInput(): void {
    this.tagInput.nativeElement.value = '';
    this.inputFormControl.setValue(null);
  }

  public removeChip(tag: StoryTag): void {
    const index: number = this.selectedItemList.findIndex(
      (itemToEvaluate: StoryTag): boolean =>
        itemToEvaluate.id === tag.id,
    );

    if (index >= 0) {
      this.selectedItemList.splice(index, 1);
      this.selectedItemIdList.splice(index, 1);
      this.valueChange.emit(this.selectedItemList);
    }
  }


  public optionSelected(event: MatAutocompleteSelectedEvent): void {
    this.optionBeingSelected = true;
    const selectedItem: StoryTag = event.option.value;
    const index: number = this.selectedItemIdList.indexOf(
      selectedItem.id,
    );
    if (index === -1) {
      this.selectedItemList.push(selectedItem);
      this.selectedItemIdList.push(selectedItem.id);
      this.valueChange.emit(this.selectedItemList);
    }
    this.tagInput.nativeElement.value = '';
    this.inputFormControl.setValue(null);
    // Mark as selection already made after a second
    setTimeout(() => this.optionBeingSelected = false, 1000);
  }

}
